import { useMutation, useQueryClient } from '@northladder/services';
import { IApiError } from '@northladder/utilities';

import { INV_MGMT_SERVICE_API } from '../../../InventoryManagementClient';
import {
  IScanPodOrTrayCodePayload,
  IScanPodOrTrayCodeResponse,
} from '../../../types';

import { TRANSFER_LIST_QUERY_KEY } from './useTransferListQuery';
import { TRANSFER_STATS_QUERY_KEY } from './useTransferStatsQuery';

/**
 * This is to track the mutation key for scanning a pod or tray code
 */
const MUT_SCAN_POD_OR_TRAY_CODE = 'scan-pod-or-tray-code-mutation';

interface IScanPodOrTrayCodeMutationParam {
  onSuccess: (data: IScanPodOrTrayCodeResponse) => void;
  onError: (err: IApiError) => void;
}

interface IScanPodOrTrayCodeMutationResult {
  data: IScanPodOrTrayCodeResponse | undefined;
  isScanPodOrTrayCodeError: boolean;
  isScanPodOrTrayCodeOngoing: boolean;
  resetScanPodOrTrayCode: () => void;
  scanPodOrTrayCode: (variables: IScanPodOrTrayCodePayload) => void;
  scanPodOrTrayCodeError: IApiError | null;
}

/**
 * React Query mutation function for scanning a pod or tray code for inventory transfer.
 */
export const useScanPodOrTrayCodeMutation = ({
  onSuccess,
  onError,
}: IScanPodOrTrayCodeMutationParam): IScanPodOrTrayCodeMutationResult => {
  const queryClient = useQueryClient();

  const { isPending, isError, error, data, mutate, reset } = useMutation({
    mutationKey: [MUT_SCAN_POD_OR_TRAY_CODE],
    networkMode: 'always',
    mutationFn: INV_MGMT_SERVICE_API.scanPodOrTrayCode,
    onSuccess: (resData) => {
      queryClient.invalidateQueries({ queryKey: [TRANSFER_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TRANSFER_STATS_QUERY_KEY] });
      onSuccess(resData);
    },
    onError,
  });

  return {
    data,
    isScanPodOrTrayCodeError: isError,
    isScanPodOrTrayCodeOngoing: isPending,
    resetScanPodOrTrayCode: reset,
    scanPodOrTrayCode: mutate,
    scanPodOrTrayCodeError: error || null,
  };
};
